import React, { useState, useEffect, useCallback, useRef } from "react";
// `testOfChangeError` is provided by the parent `DetailsContainer` via props; do not read from store here
import Table, { TableColumn, TableRow } from "../../../../components/common/Table/Table";
import Pagination from "../../../../components/common/Table/Pagination";
import CardTable from "../../../../components/common/CardTable";
import { DropdownOption } from "../../../../components/common/Dropdown";
import { ValidationError } from "../../../../components/common/ErrorBanner";
import useDeleteModal from "../ProjectHelper/useDeleteModal";
import DeleteModal from "../ProjectHelper/DeleteModal";
import DeleteBanner from "../ProjectHelper/DeleteBanner";
import {
  ProjectFailureBanner,
  ProjectSuccessBanner,
  useProjectBanner,
} from "../ProjectHelper/ProjectStatusBanner";
import ManageTestOfChange from '../Container/ManageTestOfChange';
import DisplayTestOfChange from '../Container/DisplayTestOfChange';
import NoResultsFound from "../../../../components/common/NoResultsFound";
import { formatTimestampMDY } from "../../../../utils/date";
import {
  hasTestOfChangeFormChanged,
  createTestOfChangeFormSnapshot,
  type TestOfChangeFormData,
} from "../../../../utils/ProjectDetails/projectDetailsValidation";
import PopUp from "../../../../components/common/PopUp/PopUp";
import TestOfChangeHelpModal from "../ProjectHelper/TestOfChangeHelpModal";
import TocFormExampleModal from "../ProjectHelper/TocFormExampleModal";

const NO_PROJECTS_MESSAGE =
  "Atleast one Performance Project should exist to add Tests Of change. Start adding Project from Project details tab";
const NO_TESTS_FOR_SELECTED_PROJECT_MESSAGE =
  "There are no Test Added for the selected Project";


// Columns are defined inside the component so we can conditionally include the `action` column

export const TestOfChangeTab: React.FC<{
  isAdmin?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  hasProjects?: boolean;
  onDeleteTestOfChange?: (tocId: number) => Promise<any> | void;
  selectedTestOfChange?: any;
  setSelectedTestOfChange?: (t: any) => void;
  selectedProject?: any;
  testOfChangeList?: any[];
  testOfChangeCurrentPage?: number;
  testOfChangePerPage?: number;
  testOfChangeTotalItems?: number;
  onSetTestsOfChangePage?: (p: number) => void;
  onSetTestsOfChangeSort?: (col: string, dir: "asc" | "desc") => void;
  onAddTestOfChange?: (payload: any) => Promise<{ success: boolean; error?: any }>;
  onEditTestOfChange?: (tocId: number, payload: any) => Promise<{ success: boolean; error?: any }>;
  ideaSourceOptions?: DropdownOption[];
  ratingOptions?: DropdownOption[];
  onSetUnsavedChanges?: (hasChanges: boolean) => void;
}> = ({
  isAdmin,
  canEdit,
  canDelete,
  hasProjects = true,
  onDeleteTestOfChange,
  selectedTestOfChange: propSelectedTestOfChange,
  setSelectedTestOfChange,
  selectedProject,
  testOfChangeList = [],
  testOfChangeCurrentPage = 1,
  testOfChangePerPage = 20,
  testOfChangeTotalItems = 0,
  onSetTestsOfChangePage,
  onSetTestsOfChangeSort,
  onAddTestOfChange,
  onEditTestOfChange,
  ideaSourceOptions: propIdeaSourceOptions,
  ratingOptions: propRatingOptions,
  onSetUnsavedChanges,
}) => {
    const effectiveCanEdit = canEdit ?? isAdmin ?? false;
    const effectiveCanDelete = Boolean(effectiveCanEdit && (canDelete ?? isAdmin ?? false));
    // `testOfChangeError` is received from parent when there's an error; do not read from store here
    const [selectedDetailIndex, setSelectedDetailIndex] = useState(0);
    const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
    const [isTocFormExampleModalOpen, setIsTocFormExampleModalOpen] = useState(false);
    // dispatch is not used here; parent handles fetching

    // Open TOC help or form-example modals when the popup anchors are clicked
    useEffect(() => {
      const handleHelpClick = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (target.closest('[data-open-modal="toc-help"]')) {
          e.preventDefault();
          setIsHelpModalOpen(true);
        }
        if (target.closest('[data-open-modal="toc-form-example"]')) {
          e.preventDefault();
          setIsTocFormExampleModalOpen(true);
        }
      };
      document.addEventListener('click', handleHelpClick);
      return () => document.removeEventListener('click', handleHelpClick);
    }, []);

    const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
    const [uiSortColumn, setUiSortColumn] = useState<string>("dateLastUpd");
    const baseColumns: TableColumn[] = [
      { key: "dateLastUpd", label: "Last updated", isSortable: true },
      { key: "testOfChange", label: "Test of change", className: "md:max-w-[7rem]", isSortable: true },
      { key: "testData", label: "Test data" },
      { key: "ratingText", label: "Rating", isSortable: true },
      { key: "viewDetails", label: "View details" }
    ];

    const columns: TableColumn[] = [
      ...(effectiveCanDelete ? [{ key: "deleteIcon", label: "", className: "w-[48px] text-center" } as TableColumn] : []),
      ...baseColumns.slice(0, 4),
      ...(effectiveCanEdit ? [{ key: "action", label: "Action" } as TableColumn] : []),
      ...baseColumns.slice(4),
    ];
    // Add TOC form state
    const [showAddForm, setShowAddForm] = useState<boolean>(false);
    const [tocTitle, setTocTitle] = useState<string>("");
    const [tocText, setTocText] = useState<string>("");
    const [outcome, setOutcome] = useState<string>("");
    const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
    const titleRef = React.useRef<HTMLInputElement>(null);
    const tocTextRef = React.useRef<HTMLTextAreaElement>(null);
    const outcomeRef = React.useRef<HTMLTextAreaElement>(null);
    const ideaRef = React.useRef<HTMLDivElement>(null);
    const ratingRef = React.useRef<HTMLDivElement>(null);
    const {
      bannerRef: successBannerRef,
      isVisible: showSuccessBanner,
      message: successMessage,
      showBanner: triggerSuccessBanner,
      hideBanner: hideSuccessBanner,
    } = useProjectBanner('Test of change added successfully.');
    const {
      bannerRef: errorBannerRef,
      isVisible: showErrorBanner,
      message: errorMessage,
      showBanner: triggerErrorBanner,
      hideBanner: hideErrorBanner,
    } = useProjectBanner('An error occurred while adding the Test of Change.');
    const manageRef = React.useRef<HTMLDivElement>(null);
    const manageActionContainerRef = React.useRef<HTMLDivElement>(null);
    const displayRef = React.useRef<HTMLDivElement>(null);
    const [currentTocId, setCurrentTocId] = useState<number | null>(null);
    // track which row (if any) is in Edit mode
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const defaultOption = React.useMemo<DropdownOption>(() => ({ id: "", label: "Select", value: "" }), []);
    const ideaSourceOptions: DropdownOption[] = React.useMemo(() => {
      if (propIdeaSourceOptions?.length) {
        return propIdeaSourceOptions[0]?.label === "Select"
          ? propIdeaSourceOptions
          : [defaultOption, ...propIdeaSourceOptions];
      }

      return [
        defaultOption,
        { id: 1, label: "Came up with it ourselves", value: "1" },
        { id: 2, label: "Found it in UBT Tracker", value: "2" },
        { id: 3, label: "Found it in Performance Improvement Wiki", value: "3" },
        { id: 4, label: "Idea from Sponsor/Supervisor", value: "4" },
        { id: 5, label: "Found it from another KP source", value: "5" },
        { id: 6, label: "Found it from a source outside of KP", value: "6" },
        { id: 7, label: "Idea from Member/Patient", value: "7" },
      ];
    }, [defaultOption, propIdeaSourceOptions]);
    const [selectedIdeaSource, setSelectedIdeaSource] = useState<DropdownOption>(defaultOption);
    const handleSelectIdeaSource = (opt: DropdownOption) => {
      setSelectedIdeaSource(opt);
      setValidationErrors((prev) => prev.filter((error) => error.field !== 'ideaSource'));
    };

    /**
     * Clears the 'title' validation error as soon as the user starts typing,
     * removing both the inline indicator and the top banner entry.
     */
    const handleTocTitleChange = useCallback((s: string) => {
      setTocTitle(s);
      setValidationErrors((prev) => prev.filter((error) => error.field !== 'title'));
    }, []);

    /**
     * Clears the 'testOfChange' validation error as soon as the user starts typing.
     */
    const handleTocTextChange = useCallback((s: string) => {
      setTocText(s);
      setValidationErrors((prev) => prev.filter((error) => error.field !== 'testOfChange'));
    }, []);

    /**
     * Clears the 'outcome' validation error as soon as the user starts typing.
     */
    const handleOutcomeChange = useCallback((s: string) => {
      setOutcome(s);
      setValidationErrors((prev) => prev.filter((error) => error.field !== 'outcome'));
    }, []);

    // keep selectedIdeaSource in sync when parent-provided options change
    useEffect(() => {
      if (ideaSourceOptions.length > 0) {
        const match = ideaSourceOptions.find((o) => String(o.id) === String(selectedIdeaSource?.id));
        const fallback = defaultOption;
        setSelectedIdeaSource(match ?? fallback);
      }
    }, [defaultOption, ideaSourceOptions, selectedIdeaSource?.id]);

    const defaultRatingOptions: DropdownOption[] = React.useMemo(() => {
      if (propRatingOptions?.length) {
        return propRatingOptions[0]?.label === "Select"
          ? propRatingOptions
          : [defaultOption, ...propRatingOptions];
      }

      return [
        defaultOption,
        { id: 1, label: "Adopt", value: "1" },
        { id: 2, label: "UnRated", value: "2" },
        { id: 3, label: "Abandon", value: "3" },
        { id: 4, label: "Adapt", value: "4" },
      ];
    }, [defaultOption, propRatingOptions]);

    const [selectedRating, setSelectedRating] = useState<DropdownOption>(defaultOption);

    const handleSelectRating = (opt: DropdownOption) => {
      setSelectedRating(opt);
      setValidationErrors((prev) => prev.filter((error) => error.field !== 'rating'));
    };

    // keep selectedRating in sync when parent-provided rating options change
    useEffect(() => {
      if (defaultRatingOptions.length > 0) {
        const match = defaultRatingOptions.find((o) => String(o.id) === String(selectedRating?.id));
        const fallback = defaultOption;
        setSelectedRating(match ?? fallback);
      }
    }, [defaultOption, defaultRatingOptions, selectedRating?.id]);

    // ===== Form Change Tracking =====
    const initialFormSnapshotRef = useRef<Partial<TestOfChangeFormData> | null>(null);
    const isFormOpenRef = useRef<boolean>(false);
    const [isPrepopulationComplete, setIsPrepopulationComplete] = useState<boolean>(false);
    // Tracks which editingIndex was last prepopulated. Used to prevent the prepopulation
    // effect from re-running (and resetting user edits) when dependency array items like
    // ideaSourceOptions / defaultRatingOptions get new references due to parent re-renders.
    const prepopulatedForIndexRef = useRef<number | null | undefined>(undefined);

    /**
     * Get current form data for change comparison
     */
    const getCurrentFormData = useCallback((): Partial<TestOfChangeFormData> => {
      return {
        tocTitle,
        tocText,
        outcome,
        selectedIdeaSource,
        selectedRating,
      };
    }, [tocTitle, tocText, outcome, selectedIdeaSource, selectedRating]);

    /**
     * Create initial snapshot when form opens (add or edit mode)
     */
    useEffect(() => {
      // Only create snapshot when form is shown
      if (showAddForm && !initialFormSnapshotRef.current) {
        // For Add mode, create snapshot immediately
        // For Edit mode, wait until prepopulation is complete
        const canCreateSnapshot = (editingIndex === null) || (editingIndex !== null && isPrepopulationComplete);
        
        if (canCreateSnapshot) {
          const currentData = getCurrentFormData();
          initialFormSnapshotRef.current = createTestOfChangeFormSnapshot(currentData);
          isFormOpenRef.current = true;
        }
      }

      // Reset snapshot when form closes
      if (!showAddForm && initialFormSnapshotRef.current) {
        initialFormSnapshotRef.current = null;
        isFormOpenRef.current = false;
        if (typeof onSetUnsavedChanges === 'function') {
          onSetUnsavedChanges(false);
        }
      }
    }, [showAddForm, editingIndex, isPrepopulationComplete, getCurrentFormData, onSetUnsavedChanges]);

    /**
     * Track form changes and notify parent component
     */
    useEffect(() => {
      // Only track changes after the form has been opened and snapshot created
      if (!isFormOpenRef.current || !initialFormSnapshotRef.current) {
        return;
      }

      const currentData = getCurrentFormData();
      const hasChanges = hasTestOfChangeFormChanged(currentData, initialFormSnapshotRef.current);

      if (typeof onSetUnsavedChanges === 'function') {
        onSetUnsavedChanges(hasChanges);
      }
    }, [getCurrentFormData, onSetUnsavedChanges, tocTitle, tocText, outcome, selectedIdeaSource, selectedRating]);

    /**
     * Reset unsaved changes state when form is closed or saved successfully
     */
    const resetUnsavedChanges = useCallback(() => {
      initialFormSnapshotRef.current = null;
      isFormOpenRef.current = false;
      setIsPrepopulationComplete(false);
      if (typeof onSetUnsavedChanges === 'function') {
        onSetUnsavedChanges(false);
      }
    }, [onSetUnsavedChanges]);
    // ===== End Form Change Tracking =====

    const {
      showDeleteModal,
      setShowDeleteModal,
      deletedResponseName,
      setDeletedResponseName,
      showDeleteBanner,
      setShowDeleteBanner,
      responseForms,
      setResponseForms,
    } = useDeleteModal();

    const validateTocForm = (): ValidationError[] => {
      const errors: ValidationError[] = [];
      if (!tocTitle || tocTitle.trim() === "") {
        errors.push({ field: 'title', message: 'Title is required.', fieldRef: titleRef });
      }
      if (!tocText || tocText.trim() === "") {
        errors.push({ field: 'testOfChange', message: 'Test Of Change is required.', fieldRef: tocTextRef });
      }
      if (!outcome || outcome.trim() === "") {
        errors.push({ field: 'outcome', message: 'Outcome is required.', fieldRef: outcomeRef });
      }
      if (!selectedIdeaSource?.value) {
        errors.push({ field: 'ideaSource', message: 'Idea source is required.', fieldRef: ideaRef as React.RefObject<HTMLElement> });
      }
      if (!selectedRating?.value) {
        errors.push({ field: 'rating', message: 'Rating is required.', fieldRef: ratingRef as React.RefObject<HTMLElement> });
      }
      return errors;
    };

    const handleValidationErrors = (errors: ValidationError[]): void => {
      setValidationErrors(errors);
      setShowAddForm(true);
      const ref = (errors[0]?.fieldRef?.current ?? titleRef.current) as any;
      if (ref) {
        try {
          if (typeof ref.scrollIntoView === 'function') {
            ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          // do not call focus here; only scroll to the field for accessibility tests
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Error focusing/scrolling title ref:', err);
        }
      }
    };

    const resetTocForm = (): void => {
      setTocTitle("");
      setTocText("");
      setOutcome("");
      setSelectedIdeaSource(defaultOption);
      setSelectedRating(defaultOption);
      setShowAddForm(false);
      setValidationErrors([]);
    };

    const handleAddTocSuccess = (isEdit = false): void => {
      resetTocForm();
      // clear editing state so Edit button resets
      setEditingIndex(null);
      setCurrentTocId(null);
      resetUnsavedChanges();
      triggerSuccessBanner(isEdit ? 'Test of change updated successfully.' : 'Test of change added successfully.');
    };

    const handleAddTocError = (error: any): void => {
      const msg = error?.message ?? 'Failed to add Test of Change. Please try again.';
      triggerErrorBanner(msg);
    };

    const getDeleteButton = (testOfChange: any) => (
      <button
        type="button"
        onClick={() => handleDeleteClick(testOfChange)}
        className="bg-transparent border-0 p-0 cursor-pointer"
        aria-label="Delete"
      >
        <img
          src="/assets/images/trash-outline.svg"
          alt="Delete icon"
          className="min-w-6 min-h-6"
          width={24}
          height={24}
          style={{ display: 'block' }}
        />
      </button>
    );

    const handleDeleteClick = useCallback((selectedRowOrId: any) => {
      let testOfchangeObj: any = null;
      if (selectedRowOrId && typeof selectedRowOrId === 'object') {
        testOfchangeObj = selectedRowOrId;
      } else {
        const id = Number(selectedRowOrId);
        if (!Number.isNaN(id)) {
          testOfchangeObj = testOfChangeList?.find((t: any) => String(t.tocId) === String(id)) ?? null;
        }
      }
      const testOfChangeId = Number(testOfchangeObj?.tocId ?? selectedRowOrId);
      if (Number.isNaN(testOfChangeId) && testOfChangeId !== 0) return;

      setCurrentTocId(testOfChangeId);
      setResponseForms([
        {
          id: String(testOfChangeId),
          title: testOfchangeObj?.testOfChangeTitle ?? "",
          subject: "",
          message: "",
        },
      ]);
      setShowDeleteModal(true);
    }, [testOfChangeList]);

    const handleCancelDelete = useCallback(() => {
      setShowDeleteModal(false);
    }, []);

    const handleConfirmDelete = useCallback(async () => {
      if (!currentTocId) return;

      // Close modal immediately so UI does not block while async delete runs
      setShowDeleteModal(false);

      if (typeof (onDeleteTestOfChange as any) === "function") {
        try {
          const result = await (onDeleteTestOfChange as any)(currentTocId);
          if (result?.success) {
            // Save the current response name before deleting
            const deletedResponseName = responseForms[0]?.title || '';
            // Show delete success banner
            setDeletedResponseName(deletedResponseName);
            setShowDeleteBanner(true);

            // Hide banner after 5 seconds
            setTimeout(() => {
              setShowDeleteBanner(false);
            }, 5000);

            // Reset to empty form
            setResponseForms([{ id: '1', title: '', subject: '', message: '' }]);
          } else {
            handleDeleteTestOfChangeError(result);
          }
        } catch (err) {
          const msg = extractErrorMessage(err);
          triggerErrorBanner(msg);
        }
      } else {
        return;
      }
    }, [currentTocId]);


    const handleDeleteTestOfChangeError = (result: any): void => {
      const errorCandidate = result?.error;
      const msg =
        result?.message ??
        (typeof errorCandidate === 'string' ? errorCandidate : undefined) ??
        errorCandidate?.message ??
        errorCandidate?.response?.data?.message ??
        errorCandidate?.response?.data?.error ??
        "Failed to delete test of change. Please try again.";
      triggerErrorBanner(msg);
    };

    const extractErrorMessage = (err: unknown): string => {
      if (err instanceof Error) {
        return err.message;
      } else if (typeof err === 'object' && err !== null) {
        const errorObject = err as any;
        return (
          errorObject?.message ??
          errorObject?.response?.data?.message ??
          errorObject?.response?.data?.error ??
          errorObject?.response?.statusText ??
          "Error saving test data."
        );
      }
      return String(err) || "Error saving test data.";
    };

    const buildPayload = (): any => ({
      testOfChangeTitle: tocTitle,
      testOfChange: tocText,
      outcome: outcome,
      sourceId: selectedIdeaSource?.id === defaultOption.id ? undefined : selectedIdeaSource?.id,
      ratingId: selectedRating?.id === defaultOption.id ? undefined : selectedRating?.id,
      projectId: selectedProject?.perfId ?? undefined,
    });

    const performEdit = async (payload: any) => {
      try {
        const fn = onEditTestOfChange as any;
        const res = await fn(currentTocId as number, payload);
        if (res?.success) {
          handleAddTocSuccess(true);
        } else {
          handleAddTocError(res?.error);
        }
      } catch (err) {
        const error = err instanceof Error ? { message: err.message } : { message: 'Error editing Test of Change.' };
        handleAddTocError(error);
      } finally {
        resetTocForm();
      }
    };

    const performAdd = async (payload: any) => {
      try {
        const fn = onAddTestOfChange as any;
        const res = await fn(payload);
        if (res?.success) {
          handleAddTocSuccess(false);
        } else {
          handleAddTocError(res?.error);
        }
      } catch (err) {
        const error = err instanceof Error ? { message: err.message } : { message: 'Error adding Test of Change.' };
        handleAddTocError(error);
      }
    };

    const handleAddToc = async (e?: React.MouseEvent<HTMLButtonElement>) => {
      if (e) e.preventDefault();

      if (!hasProjects) {
        return;
      }

      const errors = validateTocForm();
      if (errors.length > 0) {
        handleValidationErrors(errors);
        return;
      }

      const payload = buildPayload();

      if (currentTocId && typeof (onEditTestOfChange as any) === 'function') {
        // Guard against unnecessary saves: if the user opened Edit without making any
        // changes, show an informational message instead of calling the API.
        if (
          initialFormSnapshotRef.current &&
          !hasTestOfChangeFormChanged(getCurrentFormData(), initialFormSnapshotRef.current)
        ) {
          triggerErrorBanner('No changes found. Make changes to save.');
          return;
        }
        await performEdit(payload);
        return;
      }

      if (typeof (onAddTestOfChange as any) === 'function') {
        await performAdd(payload);
        return;
      }

      resetTocForm();
    };

    const handleOpenAddForm = () => {
      // Reset form state for creating a new Test Of Change
      setShowAddForm(true);
      // clear active detail selection while adding
      setSelectedDetailIndex(-1);
      setTocTitle("");
      setTocText("");
      setOutcome("");
      setSelectedRating(defaultOption);
      setSelectedIdeaSource(defaultOption);
      setEditingIndex(null);
      setCurrentTocId(null);
      setValidationErrors([]);
      hideErrorBanner();
      hideSuccessBanner();
    };

    const handleCancelForm = () => {
      // If we were editing a specific row, exit edit mode and show its details
      const prevEditingIndex = editingIndex;
      if (prevEditingIndex === null) {
        // If cancelling from Add, restore to first item if available
        setSelectedDetailIndex(testOfChangeList && testOfChangeList.length > 0 ? 0 : -1);
      } else {
        setEditingIndex(null);
        setSelectedDetailIndex(prevEditingIndex);
      }
      setValidationErrors([]);
      setShowAddForm(false);
      resetUnsavedChanges();
    };

    const handleToggleEdit = useCallback((index: number) => {
      // When clicking edit for the row that's already in editing mode,
      // do nothing (do not toggle off). Otherwise, enter edit mode for the row
      // and select its detail.
      if (editingIndex === index) {
        return;
      }
      setEditingIndex(index);
      setSelectedDetailIndex(index);
      const item = testOfChangeList[index] ?? null;
      if (setSelectedTestOfChange) setSelectedTestOfChange(item);
    }, [editingIndex, testOfChangeList, setSelectedTestOfChange]);

    // when entering edit mode, prefill the add/edit form with the selected row
    useEffect(() => {
      if (editingIndex === null) {
        // Reset prepopulation flag when not in edit mode
        setIsPrepopulationComplete(false);
        prepopulatedForIndexRef.current = undefined;
        return;
      }

      // Guard: only prepopulate once per editingIndex value. Without this, any parent
      // re-render that creates new ideaSourceOptions / defaultRatingOptions references
      // (e.g. because setHasUnsavedChanges fires) would re-run this effect and overwrite
      // the user's in-progress edits with the original row values.
      if (prepopulatedForIndexRef.current === editingIndex) {
        return;
      }
      prepopulatedForIndexRef.current = editingIndex;

      const item = testOfChangeList[editingIndex] ?? null;
      setShowAddForm(true);
      setCurrentTocId(item?.tocId ?? null);
      setTocTitle(item?.testOfChangeTitle ?? item?.testOfChange ?? "");
      setTocText(item?.testOfChange ?? "");
      setOutcome(item?.outcome ?? "");

      const ideaOpt = ideaSourceOptions.find((o) => String(o.id) === String(item?.testSourceId ?? item?.ideaSourceId ?? item?.source)) ?? defaultOption;
      setSelectedIdeaSource(ideaOpt);
      const ratingOpt = defaultRatingOptions.find((o) => String(o.id) === String(item?.ratingId ?? item?.rating)) ?? defaultOption;
      setSelectedRating(ratingOpt);

      // Mark prepopulation as complete for snapshot timing
      setIsPrepopulationComplete(true);
    }, [defaultOption, defaultRatingOptions, editingIndex, ideaSourceOptions, testOfChangeList]);

    // If we switch from editing (editingIndex was non-null) to Add mode while
    // `showAddForm` is already true, ensure we still scroll the manage form.
    useEffect(() => {
      if (!showAddForm) return;
      if (editingIndex !== null) return; // only care when not editing
      let raf = 0 as any;
      const tryScroll = () => {
        const ok = focusManage();
        if (!ok) raf = requestAnimationFrame(tryScroll);
      };
      raf = requestAnimationFrame(tryScroll);
      return () => { if (raf) cancelAnimationFrame(raf); };
    }, [editingIndex, showAddForm]);

    // When add/edit form is shown, scroll to and focus the ManageTestOfChange title input
    const focusManage = () => {
      const ref = titleRef.current ?? manageRef.current?.querySelector('input, textarea, [tabindex]') as HTMLElement | null;
      if (ref) {
        try {
          if (typeof ref.scrollIntoView === 'function') ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch (e) {
          console.error(e)
        }
        // do not focus; only scroll into view
        return true;
      }
      return false;
    };

    useEffect(() => {
      if (!showAddForm) return;
      // wait for the manage form to mount and refs to attach
      let raf = 0 as any;
      const tryFocus = () => {
        const ok = focusManage();
        if (!ok) raf = requestAnimationFrame(tryFocus);
      };
      raf = requestAnimationFrame(tryFocus);
      return () => { if (raf) cancelAnimationFrame(raf); };
    }, [showAddForm]);

    // Also focus when editingIndex changes (covers switching between Add and Edit without showAddForm toggling)
    useEffect(() => {
      if (editingIndex === null) return;
      let raf = 0 as any;
      const tryFocus = () => {
        const ok = focusManage();
        if (!ok) raf = requestAnimationFrame(tryFocus);
      };
      raf = requestAnimationFrame(tryFocus);
      return () => { if (raf) cancelAnimationFrame(raf); };
    }, [editingIndex]);

    // When view details changes (and not in edit/add mode), scroll to and focus the DisplayTestOfChange container
    useEffect(() => {
      // Only scroll display when not editing/adding
      if (showAddForm || editingIndex !== null) return;
      let raf = 0 as any;
      const tryScrollDisplay = () => {
        const el = displayRef.current as HTMLElement | null;
        if (!el) {
          raf = requestAnimationFrame(tryScrollDisplay);
          return;
        }
        try {
          if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch (e) {
          console.error(e);
        }
      };
      raf = requestAnimationFrame(tryScrollDisplay);
      return () => { if (raf) cancelAnimationFrame(raf); };
    }, [selectedDetailIndex, showAddForm, editingIndex]);


    // Tests-of-change data and fetching are handled by the parent `DetailsContainer` and
    // are provided via props: `testOfChangeList`, `testOfChangeCurrentPage`, etc.

    const handleTestOfChangeListSort = useCallback(
      (columnKey: string, direction: "asc" | "desc") => {
        const apiColumnMap: { [key: string]: string } = {
          testOfChange: 'testofchange',
          dateLastUpd: 'datelastupd',
          ratingText: 'rating',
        };
        const apiSortColumn = apiColumnMap[columnKey] ?? columnKey.toUpperCase();
        setUiSortColumn(columnKey);
        setSortDirection(direction);
        // Tell parent to update sort and reset to page 1
        if (onSetTestsOfChangeSort) onSetTestsOfChangeSort(apiSortColumn, direction);
        if (onSetTestsOfChangePage) onSetTestsOfChangePage(1);
      },
      [onSetTestsOfChangePage, onSetTestsOfChangeSort],
    );

    const handleTestOfChangePageChange = useCallback((page: number) => {
      // If currently editing or adding, exit edit/add mode before paginating so the UI resets
      if (editingIndex !== null || showAddForm) {
        setEditingIndex(null);
        setShowAddForm(false);
        // clear selected detail while page refreshes
        setSelectedDetailIndex(-1);
      }
      if (onSetTestsOfChangePage) onSetTestsOfChangePage(page);
    }, [onSetTestsOfChangePage, editingIndex, showAddForm]);

    const handleViewDetails = (index: number) => {
      // stop any edit mode when switching to view details
      setEditingIndex(null);
      // ensure the add/edit form is hidden so the details pane can render
      setShowAddForm(false);
      if (index >= 0 && index < testOfChangeList.length) {
        setSelectedDetailIndex(index);
        const item = testOfChangeList[index] ?? null;
        if (setSelectedTestOfChange) setSelectedTestOfChange(item);
      }
    };

    useEffect(() => {
      // if parent provided a selected item, use it; otherwise default to first
      if (propSelectedTestOfChange) {
        const idx = testOfChangeList.findIndex((t: any) => (t.tocId ?? t.id) === (propSelectedTestOfChange.tocId ?? propSelectedTestOfChange.id));
        if (idx >= 0) {
          // use relative index within the current results
          setSelectedDetailIndex(idx);
          const item = testOfChangeList[idx] ?? null;
          if (setSelectedTestOfChange) setSelectedTestOfChange(item);
          return;
        }
      }
      // default to the first visible item on the current page
      setSelectedDetailIndex(0);
      const first = testOfChangeList[0] ?? null;
      if (setSelectedTestOfChange) setSelectedTestOfChange(first);
    }, [testOfChangeList, propSelectedTestOfChange]);

    const rowsWithActions: TableRow[] = testOfChangeList.map((testOfChange, idx) => {
      const isEdit = editingIndex === idx;
      const isActive = selectedDetailIndex === idx && !isEdit;
      const btnClassEdit = `button py-0! ${isEdit ? 'btn-primary group bg-[#003B71]! text-white!' : '-inverted hover:bg-[#003B71]! group hover:text-white!'}`;
      const btnClassView = `button py-0! ${isActive ? 'btn-primary group bg-[#003B71]! text-white!' : 'hover:bg-[#003B71]! group hover:text-white!'}`;
      return {
        rowKey: `testOfChange-${testOfChange.tocId}-0`,
        deleteIcon: effectiveCanDelete ? getDeleteButton(testOfChange) : null,
        dateLastUpd: formatTimestampMDY(testOfChange.dateLastUpd) ?? "",
        testOfChange: (
          <button
            type="button"
            className="underline text-blue-600 hover:text-blue-700 bg-transparent border-0 p-0 cursor-pointer text-left"
            style={{ color: '#0B69A3', display: 'block', width: '100%', textAlign: 'left' }}
            onClick={(e) => {
              e.preventDefault();
              // select this item in the detail pane and hide any add/edit form
              setEditingIndex(null);
              setShowAddForm(false);
              setSelectedDetailIndex(idx);
              const item = testOfChangeList[idx] ?? null;
              if (setSelectedTestOfChange) setSelectedTestOfChange(item);
            }}
          >
            {testOfChange.testOfChangeTitle ?? ''}
          </button>
        ),
        testOfChangeDesc: testOfChange.testOfChange ?? "",
        testOfChangeTitle: testOfChange.testOfChangeTitle ?? testOfChange.testOfChange ?? "",
        testSourceId: testOfChange.testSourceId ?? "",
        testSourceDesc: testOfChange.testSourceDesc ?? testOfChange.testSource ?? "",
        outcome: testOfChange.outcome ?? "",
        ideaSource: testOfChange.ideaSource ?? "",
        testData: (
          <button
            type="button"
            className="underline text-blue-600 hover:text-blue-700 bg-transparent border-0 p-0 cursor-pointer"
            style={{ color: '#0B69A3' }}
            onClick={(e) => {
              e.preventDefault();
              // select this item in the detail pane
              setSelectedDetailIndex(idx);
              const item = testOfChangeList[idx] ?? null;
              if (setSelectedTestOfChange) setSelectedTestOfChange(item);
              // attempt to activate the Test Data tab (third tab)
              try {
                const tabLinks = document.querySelectorAll('.ds-tabs__link');
                const testDataTabIndex = 2; // zero-based index for third tab
                if (tabLinks && tabLinks.length > testDataTabIndex) {
                  (tabLinks[testDataTabIndex] as HTMLElement).click();
                }
              } catch (err) {
                // eslint-disable-next-line no-console
                console.warn('Could not activate Test Data tab', err);
              }
            }}
          >
            Add/View data
          </button>
        ),
        ratingText: `${testOfChange.ratingId ?? ""}-${testOfChange.ratingText ?? ""}`,
        action: effectiveCanEdit ? (
          <button
            type="button"
            className={btnClassEdit}
            onClick={() => handleToggleEdit(idx)}
          >
            <img
              src="/assets/images/pencil-outline.svg"
              alt=""
              className="min-w-6 min-h-6 group-focus:brightness-0! group-focus:invert! group-hover:brightness-0! group-hover:invert!"
            />{' '}
            {isEdit ? 'Editing' : 'Edit'}

          </button>
        ) : null,
        viewDetails: (
          <button
            type="button"
            className={btnClassView}
            onClick={() => handleViewDetails(idx)}
          >
            {isActive ? 'Viewing' : 'View details'}

          </button>
        )
      };
    });


    // When there are no projects, don't return early — render the NoResultsFound
    // block in the UI where the project header would normally appear.

    return (
      <>
        {hasProjects ? (
          <div className="pt-4  gap-4 mb-8! relative">
            <div className=" items-center gap-4 w-full">
              <div>
                <DeleteBanner show={showDeleteBanner} bannerText={deletedResponseName} />
                <ProjectSuccessBanner
                  show={showSuccessBanner}
                  message={successMessage}
                  bannerRef={successBannerRef}
                />
                <ProjectFailureBanner
                  show={showErrorBanner}
                  message={errorMessage}
                  bannerRef={errorBannerRef}
                />
              </div>
              <div className="flex items-center gap-4 mt-8!">
                <p className="p3"><strong>Name of Project: </strong></p>
                <p className="p3">{selectedProject?.nameOfProject ?? selectedProject?.pipDesc ?? ''}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="mb-4! w-full mt-10!">
            <NoResultsFound
              dsCardClassName="w-full!"
              title="Alert"
              message={NO_PROJECTS_MESSAGE}
            />
          </div>
        )}

        {/* Add Test Of Change form below the table */}
        <div className="mb-8!">
            <div className="flex mb-0! justify-between w-full! justify-items-center">
              <div className="flex items-center">
                  { effectiveCanEdit && (
                    <button
                      aria-label="Toggle Add Test Of Change"
                      className={`button py-0! -primary -light flex items-center group hover:text-white! focus:text-white!`}
                      onClick={() => handleOpenAddForm()}
                    >
                      <img
                        src="/assets/images/plus-outline-white.svg"
                        alt="Plus icon"
                        className="min-w-6 min-h-6 group-focus:brightness-0! group-focus:invert! group-hover:brightness-0! group-hover:invert!"
                        style={{ verticalAlign: 'middle', filter: 'brightness(2)' }}
                      />
                      {' '}
                      Add New Test
                    </button>
                  )}
                  <div ref={manageActionContainerRef} className="flex items-center gap-3 ml-3!" />
              </div>
              <div className="flex gap-1 text-kp-blue-dark! items-center">
                <p className="font-semibold">Help</p>
                <span className="inline-flex items-centergap-1"><PopUp className="ml-1! -medium"
                  headerText="Test Of Change Page Help"
                  message={`Click <a href="#" data-open-modal="toc-form-example"> here </a>for an example of a completed form <br/><br/>
                Click <a href="#" data-open-modal="toc-help">here </a> for additional page help <br/><br/>
                Click <a target="_blank" rel="noopener noreferrer" href="https://www.lmpartnership.org/path-to-performance/ubt-basics">here </a> for the National LMP web site`}
                /></span>
              </div>
            </div>
        </div>
        <div>
          {hasProjects && rowsWithActions.length === 0 && !showAddForm ? (
            <div className="mb-4! w-full mt-10!">
              <NoResultsFound
                dsCardClassName="w-full!"
                title="Alert"
                message={NO_TESTS_FOR_SELECTED_PROJECT_MESSAGE}
              />
            </div>
          ) : null}

          {rowsWithActions.length > 0 ? (

            <div className="pt-0! mb-12!">
              <div> <CardTable>

                <>
                  <Table
                    columns={columns}
                    rows={rowsWithActions}
                    onSort={(columnKey, sortDirection) => handleTestOfChangeListSort(columnKey, sortDirection.toLowerCase() as "asc" | "desc")}
                    sortColumn={uiSortColumn}
                    sortDirection={sortDirection.toUpperCase() as "ASC" | "DESC"} />
                  <div className="mt-4 mobile:relative mobile:left-7.5">
                    <div className="mt-4 mobile:relative mobile:left-7.5">
                      <Pagination
                        totalItems={testOfChangeTotalItems}
                        itemsPerPage={testOfChangePerPage}
                        currentPage={testOfChangeCurrentPage}
                        onPageChange={handleTestOfChangePageChange} />
                    </div>
                  </div>
                </>

              </CardTable>
              </div>
            </div>
          ) : null}

          {/* Show details pane only when there are rows */}
          {rowsWithActions.length > 0 && editingIndex !== selectedDetailIndex && !showAddForm && (
            <div ref={displayRef}>
              <DisplayTestOfChange rows={rowsWithActions} selectedIndex={selectedDetailIndex} />
            </div>
          )}

          {/* Always render the ManageTestOfChange form so tests can open it even when no rows exist */}
          <div ref={manageRef}>
            <ManageTestOfChange
              visible={
                // show when user explicitly opened the add form, or when entering edit mode
                showAddForm || (editingIndex !== null && editingIndex === selectedDetailIndex)
              }
              validationErrors={validationErrors}
              isEditing={editingIndex !== null}
              titleRef={titleRef}
              tocTitle={tocTitle}
              setTocTitle={handleTocTitleChange}
              tocTextRef={tocTextRef}
              tocText={tocText}
              setTocText={handleTocTextChange}
              outcomeRef={outcomeRef}
              outcome={outcome}
              setOutcome={handleOutcomeChange}
              ideaRef={ideaRef}
              selectedIdeaSource={selectedIdeaSource}
              ideaSourceOptions={ideaSourceOptions}
              handleSelectIdeaSource={handleSelectIdeaSource}
              ratingRef={ratingRef}
              selectedRating={selectedRating}
              defaultRatingOptions={defaultRatingOptions}
              handleSelectRating={handleSelectRating}
              onCancel={handleCancelForm}
              onSave={handleAddToc}
              actionContainerRef={manageActionContainerRef}
            />
          </div>

          <div>
            <DeleteModal isOpen={showDeleteModal} title="Delete Test Of Change" deleteInfo={"Are you sure you want to delete this Test of Change?"} deleteButtonInfo="Delete Test" onCancel={handleCancelDelete} onConfirm={handleConfirmDelete} />
          </div>
        </div>
        <TestOfChangeHelpModal isOpen={isHelpModalOpen} onClose={() => setIsHelpModalOpen(false)} />
        <TocFormExampleModal isOpen={isTocFormExampleModalOpen} onClose={() => setIsTocFormExampleModalOpen(false)} />
      </>
    );
  };



